import {
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useState,
} from 'react';
import type { Dispatch, Ref, SetStateAction } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CountrySelector } from '@/components/ui/country-selector';
import { BankNameCombobox } from '@/components/banking/BankNameCombobox';
import { IbanInput, type IbanStatus } from '@/components/banking/IbanInput';
import {
  useValidateIban,
  useValidateSwift,
} from '@/portal/profile/hooks/useInvestors';
import type { IbanValidation } from '@/types/banking';

type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

function statusBorder(status: ValidationStatus): string {
  switch (status) {
    case 'valid':
      return 'border-success';
    case 'validating':
      return 'border-warning';
    case 'invalid':
      return 'border-destructive';
    default:
      return '';
  }
}

// Trailing spinner / tick / cross reflecting the validation status, mirroring
// the staff banking form so the portal gives the same at-a-glance feedback.
function StatusAdornment({ status }: { status: ValidationStatus }) {
  if (status === 'validating') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-foreground" />
      </div>
    );
  }
  if (status === 'valid' || status === 'invalid') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {status === 'valid' ? (
          <span className="text-success">✓</span>
        ) : (
          <span className="text-destructive">✗</span>
        )}
      </div>
    );
  }
  return null;
}

function errorBorder(error: string | undefined): string {
  return error ? 'border-destructive' : '';
}

function FieldMessage({ error, hint }: { error?: string; hint?: string }) {
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!hint) return null;
  return <p className="text-sm text-muted-foreground">{hint}</p>;
}

function bankNameHint(hasValidIban: boolean, locked: boolean): string {
  if (locked) return 'Matched from the IBAN and managed in the bank registry.';
  return hasValidIban
    ? 'No registry match for this IBAN - type the bank name.'
    : 'Matched from the SWIFT/BIC when recognised, or type it manually.';
}

function savedValue(values: Record<string, unknown>, key: string): string {
  return values[key] ? String(values[key]).trim() : '';
}

// Banking fields a change request can carry; used to tell whether the user has
// actually edited banking before enforcing the non-IBAN country requirement.
const BANKING_KEYS = [
  'bank_account_name',
  'iban',
  'bank_account_number',
  'bank_swift_code',
  'bank_name',
  'bank_country',
  'bank_address',
];

export interface BankingSectionHandle {
  /**
   * Validate the banking fields on submit. Returns false (and surfaces an
   * inline error) when a non-IBAN account is missing its bank country.
   */
  validate: () => boolean;
}

interface BankingSectionProps {
  isOpen: boolean;
  currentValues: Record<string, unknown>;
  newValues: Record<string, string>;
  setNewValues: Dispatch<SetStateAction<Record<string, string>>>;
  ref?: Ref<BankingSectionHandle>;
}

export function BankingSection({
  isOpen,
  currentValues,
  newValues,
  setNewValues,
  ref,
}: BankingSectionProps) {
  const [ibanStatus, setIbanStatus] = useState<IbanStatus>('idle');
  const [swiftStatus, setSwiftStatus] = useState<ValidationStatus>('idle');
  const [bankCountry, setBankCountry] = useState('');
  const [bankNameSuggestion, setBankNameSuggestion] = useState('');
  const [autoFilled, setAutoFilled] = useState<{
    bank_swift_code: boolean;
    bank_name: boolean;
  }>({ bank_swift_code: false, bank_name: false });
  // Set on submit when a non-IBAN account is missing a required field, keyed by
  // field name (bank_account_number / bank_name / bank_country).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { mutateAsync: validateIbanRequest } = useValidateIban();
  const { mutateAsync: validateSwiftRequest } = useValidateSwift();

  // The IBAN is the anchor when it validates; otherwise the form falls back to
  // an account number + SWIFT/BIC.
  const hasValidIban = ibanStatus === 'valid';

  const val = (key: string) =>
    newValues[key] ?? (currentValues[key] ? String(currentValues[key]) : '');

  const clearFieldError = (key: string) =>
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // A registry match makes the bank name authoritative, so it's shown read-only
  // (free-text entry stays available only when the IBAN matches no known bank).
  const bankNameLocked = hasValidIban && !!bankNameSuggestion;

  // Validate-on-submit: a non-IBAN account doesn't carry its routing details in
  // an identifier, so once the user has edited banking into a SWIFT/account-
  // number account the account number, bank name and bank country all become
  // required - matching the staff form and the backend.
  useImperativeHandle(ref, () => ({
    validate: () => {
      const touchedBanking = BANKING_KEYS.some((key) => key in newValues);
      if (!touchedBanking) {
        setFieldErrors({});
        return true;
      }
      const hasNonIbanDetails =
        !!val('bank_account_number').trim() || !!val('bank_swift_code').trim();
      const next: Record<string, string> = {};
      if (!hasValidIban && hasNonIbanDetails) {
        if (!val('bank_account_number').trim()) {
          next.bank_account_number =
            'Account number is required for a non-IBAN account';
        }
        if (!val('bank_name').trim()) {
          next.bank_name = 'Bank name is required for a non-IBAN account';
        }
        if (!(val('bank_country') || bankCountry).trim()) {
          next.bank_country = 'Bank country is required for a non-IBAN account';
        }
      }
      setFieldErrors(next);
      return Object.keys(next).length === 0;
    },
  }));

  const savedIban = savedValue(currentValues, 'iban');
  const savedSwift = savedValue(currentValues, 'bank_swift_code');

  // The classification logic reads the latest saved values and validation
  // mutations, but none of those should *re-trigger* it - only the dialog
  // opening should. Wrapping it as an Effect Event keeps those reads
  // non-reactive so the effect's sole dependency is `isOpen`.
  const classifySaved = useEffectEvent((isCancelled: () => boolean) => {
    if (savedSwift) {
      void validateSwiftRequest(savedSwift).then(
        (r) => {
          if (isCancelled()) return;
          setSwiftStatus(r.is_valid ? 'valid' : 'invalid');
          setBankCountry(r.is_valid ? r.country_code : '');
          setBankNameSuggestion(r.is_valid && r.bank_name ? r.bank_name : '');
        },
        () => {
          if (!isCancelled()) setSwiftStatus('invalid');
        }
      );
    }
  });

  // When the dialog opens, classify a saved non-IBAN account's SWIFT so the
  // section starts in the right mode; the IBAN field classifies a saved IBAN
  // itself. Read-only: it sets status and the country chip but never writes
  // newValues, so submit stays disabled until the user actually edits a field.
  useEffect(() => {
    if (!isOpen || savedIban) return;

    let cancelled = false;
    classifySaved(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [isOpen, savedIban]);

  // Only a validated IBAN speaks for the bank, so anything else leaves the
  // country chip and the bank-name suggestion with nothing to show.
  const handleIbanStatusChange = (status: IbanStatus) => {
    setIbanStatus(status);
    if (status !== 'valid') {
      setBankNameSuggestion('');
      setBankCountry('');
    }
  };

  // What the registry made of the IBAN the user entered, folded into the
  // pending change.
  const applyIbanValidation = (validation: IbanValidation) => {
    setSwiftStatus('idle');
    setBankCountry(validation.country_code);
    setBankNameSuggestion(validation.bank_name);
    // A valid IBAN is the account identifier, so the non-IBAN fields no
    // longer apply - drop them from the pending change.
    setNewValues((prev) => {
      const next = { ...prev };
      delete next.bank_account_number;
      delete next.bank_address;
      return next;
    });
    // Auto-fill the derived SWIFT/bank name only when the field is empty or
    // still holds a previously auto-filled value - never clobber a value the
    // user typed.
    const fillSwift =
      !!validation.swift_code &&
      (!newValues.bank_swift_code || autoFilled.bank_swift_code);
    const fillBankName =
      !!validation.bank_name && (!newValues.bank_name || autoFilled.bank_name);
    setAutoFilled({ bank_swift_code: fillSwift, bank_name: fillBankName });
    setNewValues((prev) => {
      const next = { ...prev };
      if (fillSwift) {
        next.bank_swift_code = validation.swift_code;
      }
      if (fillBankName) {
        next.bank_name = validation.bank_name;
      }
      // The IBAN country is authoritative, so capture it in the change.
      next.bank_country = validation.country_code;
      return next;
    });
  };

  // The same for the account already on file. Nothing is written into the
  // pending change, so opening the dialog doesn't make it submittable.
  const applyHydratedIban = (validation: IbanValidation) => {
    setBankCountry(validation.country_code);
    setBankNameSuggestion(validation.bank_name);
  };

  // Validate a SWIFT/BIC for a non-IBAN account and infer its country (and bank
  // name when the registry knows it). Only meaningful when there's no IBAN.
  const validateSwift = async (swift: string) => {
    if (hasValidIban || !swift.trim()) {
      setSwiftStatus('idle');
      return;
    }

    setSwiftStatus('validating');

    try {
      const result = await validateSwiftRequest(swift);
      setSwiftStatus(result.is_valid ? 'valid' : 'invalid');
      setBankCountry(result.is_valid ? result.country_code : '');
      // Surface the registry bank name (when the BIC is recognised) as the
      // one-click dropdown suggestion, mirroring the IBAN flow.
      setBankNameSuggestion(
        result.is_valid && result.bank_name ? result.bank_name : ''
      );
      // Capture the BIC-derived country in the change (a user pick still wins,
      // since selecting the field writes bank_country afterwards).
      if (result.is_valid) {
        setNewValues((prev) => ({
          ...prev,
          bank_country: result.country_code,
        }));
      }
      // Auto-fill the registry bank name only when the user hasn't typed one.
      if (result.is_valid && result.bank_name && !newValues.bank_name) {
        setNewValues((prev) => ({ ...prev, bank_name: result.bank_name }));
        setAutoFilled((prev) => ({ ...prev, bank_name: true }));
      }
    } catch {
      setSwiftStatus('invalid');
      setBankCountry('');
      setBankNameSuggestion('');
    }
  };

  // Changing the IBAN invalidates the auto-filled SWIFT and bank name, so drop
  // them (keeping any values the user typed) to avoid saving details that don't
  // match the new account.
  const handleIbanChange = (value: string) => {
    setNewValues((prev) => {
      const next: Record<string, string> = { ...prev, iban: value };
      if (autoFilled.bank_swift_code) {
        delete next.bank_swift_code;
      }
      if (autoFilled.bank_name) {
        delete next.bank_name;
      }
      return next;
    });
    setAutoFilled({ bank_swift_code: false, bank_name: false });
    // The field reports its own status; the SWIFT one was only derived from it.
    setSwiftStatus('idle');
  };

  // Editing the SWIFT in non-IBAN mode invalidates the derived country.
  const handleSwiftChange = (value: string) => {
    setNewValues((prev) => ({ ...prev, bank_swift_code: value }));
    if (autoFilled.bank_swift_code) {
      setAutoFilled((prev) => ({ ...prev, bank_swift_code: false }));
    }
    setSwiftStatus('idle');
    setBankCountry('');
    // The old BIC's suggested bank no longer applies once the code changes.
    setBankNameSuggestion('');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="bank_account_name">Account Holder Name</Label>
        <Input
          id="bank_account_name"
          value={val('bank_account_name')}
          onChange={(e) =>
            setNewValues({ ...newValues, bank_account_name: e.target.value })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="iban">IBAN</Label>
        <IbanInput
          id="iban"
          value={val('iban')}
          onChange={handleIbanChange}
          validate={validateIbanRequest}
          onValidated={applyIbanValidation}
          onHydrated={applyHydratedIban}
          onStatusChange={handleIbanStatusChange}
        />
        <p className="text-sm text-muted-foreground">
          {ibanStatus === 'invalid'
            ? 'Not a valid IBAN. Clear it to enter an account number + SWIFT.'
            : 'Have an IBAN? Enter it to auto-fill. No IBAN (e.g. US)? Leave blank.'}
        </p>
      </div>

      {!hasValidIban && (
        <div className="space-y-2">
          <Label htmlFor="bank_account_number">Account Number *</Label>
          <Input
            id="bank_account_number"
            value={val('bank_account_number')}
            placeholder="e.g. 000123456789"
            className={errorBorder(fieldErrors.bank_account_number)}
            onChange={(e) => {
              setNewValues({
                ...newValues,
                bank_account_number: e.target.value,
              });
              clearFieldError('bank_account_number');
            }}
          />
          <FieldMessage error={fieldErrors.bank_account_number} />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="bank_swift_code">SWIFT/BIC Code</Label>
        <div className="relative">
          <Input
            id="bank_swift_code"
            value={val('bank_swift_code')}
            placeholder="e.g. USBKUS44IMT"
            className={hasValidIban ? '' : statusBorder(swiftStatus)}
            onChange={(e) => handleSwiftChange(e.target.value)}
            onBlur={
              hasValidIban ? undefined : (e) => validateSwift(e.target.value)
            }
          />
          {!hasValidIban && <StatusAdornment status={swiftStatus} />}
        </div>
        <p className="text-sm text-muted-foreground">
          {hasValidIban
            ? 'Auto-filled from the IBAN - edit if the bank isn’t recognised.'
            : 'Required for an account without an IBAN.'}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="bank_name">Bank Name{!hasValidIban && ' *'}</Label>
        <BankNameCombobox
          value={val('bank_name')}
          suggestion={bankNameSuggestion}
          locked={bankNameLocked}
          className={errorBorder(fieldErrors.bank_name)}
          onChange={(value) => {
            setNewValues({ ...newValues, bank_name: value });
            clearFieldError('bank_name');
            if (autoFilled.bank_name) {
              setAutoFilled((prev) => ({ ...prev, bank_name: false }));
            }
          }}
        />
        <FieldMessage
          error={fieldErrors.bank_name}
          hint={bankNameHint(hasValidIban, bankNameLocked)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="bank_country">
          Bank Country{!hasValidIban && ' *'}
        </Label>
        <CountrySelector
          id="bank_country"
          value={val('bank_country') || bankCountry}
          onValueChange={(value) => {
            setNewValues({ ...newValues, bank_country: value });
            clearFieldError('bank_country');
          }}
          placeholder="Select bank country"
          disabled={hasValidIban}
          className={errorBorder(fieldErrors.bank_country)}
        />
        <FieldMessage
          error={fieldErrors.bank_country}
          hint={
            hasValidIban
              ? 'Set from the IBAN.'
              : 'Detected from the SWIFT/BIC - set it manually if needed.'
          }
        />
      </div>

      {!hasValidIban && (
        <div className="space-y-2">
          <Label htmlFor="bank_address">Bank Address</Label>
          <Textarea
            id="bank_address"
            value={val('bank_address')}
            placeholder="Bank branch address (for international wires)"
            rows={2}
            onChange={(e) =>
              setNewValues({ ...newValues, bank_address: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );
}
